import { DeleteModal } from "@/components/modal/DeleteModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { DeleteWithFloatingIp } from "@/modules/network/DeleteWithFloatingIp.tsx";
import { deleteIpAddressMutation, ipQueryAtom } from "@/modules/network/ipAddress/ipAddressApi.ts";
import type { ManagedServiceLoc } from "@/modules/services/servicesQuery.ts";
import type { DeleteServiceMutation } from "@/modules/services/ServiceView.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans } from "@lingui/react/macro";
import { useState } from "react";

export interface ServiceDeleteModalProps {
    managedService: ManagedServiceLoc;
    deleteMutation: DeleteServiceMutation;
}

export function ServiceDeleteModal({ managedService, deleteMutation }: ServiceDeleteModalProps) {
    //#region Hooks
    const delIpMutation = useStandardMutation(deleteIpAddressMutation);
    const { uuid, display_name, properties } = managedService;
    const ips = useSuspenseQueryAtom(ipQueryAtom(properties.location));
    const attachedPublicIp = [...ips.values()].find(
        (ip) => ip?.assigned_to_resource_type === "service" && ip.assigned_to === uuid,
    );

    const [deleteFloatingIp, setDeleteFloatingIp] = useState<boolean>();
    //#endregion

    async function deleteAction() {
        await deleteMutation.mutateAsync({ uuid });
        if (deleteFloatingIp && attachedPublicIp) {
            await delIpMutation.mutateAsync({ location: properties.location, address: attachedPublicIp.address });
        }
    }

    return (
        <DeleteModal modalAction={deleteAction} isActionDisabled={!!attachedPublicIp && deleteFloatingIp == null}>
            <p className="pb-3">
                <Trans>
                    Deleting the service "{display_name}" will permanently remove it, and it will not be accessible
                    after that.
                </Trans>
            </p>
            <p>
                <Trans>
                    Deleting a service will also delete your backups and the service cannot be restored after that.
                </Trans>
            </p>
            <DeleteWithFloatingIp
                ipAddress={attachedPublicIp}
                value={deleteFloatingIp}
                onChange={setDeleteFloatingIp}
            />
        </DeleteModal>
    );
}
